import { Dialog as D } from 'radix-ui';
import { motion } from 'motion/react';
import type { AssistantCommand, AssistantContext, QuickAction } from '@nw/web-platform';
import { AssistantCore } from './assistant/Assistant.js';
import type { Space } from './spaces.js';

/**
 * Ask: the command centre. A living orb, one line for anything typed or spoken, and below it
 * the fastest ways to act: capture something new, or jump to a space.
 */
export function AskSheet({
  open,
  onOpenChange,
  listen,
  commands,
  ctx,
  actions,
  spaces,
  onCapture,
  onGo,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  listen: boolean;
  commands: AssistantCommand[];
  ctx: Omit<AssistantContext, 'speak'>;
  actions: QuickAction[];
  spaces: Space[];
  onCapture: (id: string) => void;
  onGo: (s: Space) => void;
}) {
  const fine = typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches;
  const idle = (
    <div className="nw-ask-idle">
      {actions.length ? (
        <section aria-label="Capture">
          <h3 className="nw-label mb-2 px-1">Capture</h3>
          <ul className="nw-ask-tiles">
            {actions.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  className="nw-ask-tile"
                  onClick={() => {
                    onOpenChange(false);
                    onCapture(a.id);
                  }}
                >
                  <span className="nw-ask-tile-icon">
                    <a.icon className="size-5" />
                  </span>
                  <span className="text-[15px] font-semibold">{a.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section aria-label="Go to" className="mt-5">
        <h3 className="nw-label mb-2 px-1">Go to</h3>
        <ul className="nw-ask-tiles">
          {spaces.slice(0, 8).map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className="nw-ask-tile"
                onClick={() => {
                  onOpenChange(false);
                  onGo(s);
                }}
              >
                <span className="nw-ask-tile-icon nw-ask-tile-space" style={{ ['--h' as string]: s.hue }}>
                  <s.icon className="size-5" />
                </span>
                <span className="text-[15px] font-semibold">{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="nw-grid-overlay" />
        <D.Content aria-describedby={undefined} className="nw-ask-sheet" onOpenAutoFocus={(e) => !fine && e.preventDefault()}>
          <motion.div initial={{ opacity: 0, y: -28, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', stiffness: 420, damping: 34 }}>
            <D.Title className="sr-only">Assistant</D.Title>
            <div className="nw-ask-head">
              <span className="nw-ask-big-orb" aria-hidden>
                <span className="nw-ask-orb-glow" />
              </span>
              <p className="nw-display text-[1.7rem] leading-tight sm:text-[2.1rem]">What do you need?</p>
            </div>
            <AssistantCore variant="overlay" autoFocus={fine} autoListen={listen} commands={commands} ctx={ctx} onDone={() => onOpenChange(false)} idle={idle} />
          </motion.div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
